import React from 'react';
import PropTypes from 'prop-types';
import Stepper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Stepper';
import Step from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Step';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import StepLabel from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/StepLabel';
import { useExpandedSteps } from 'Views/Page/Content/Templates/planSummaryPageTemplate/useExpandedSteps';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import eventTypes from 'EventTracking/consts/eventTypes';
import eventSubTypes from 'EventTracking/consts/eventSubTypes';
import categories from 'EventTracking/consts/categories';
import { SubtleProgressPie } from './subtleProgressPie';
import { StepPicker } from './step/stepPicker';
import { BottomUpConnector } from './connector/bottomUpConnector';
import { OnTopOfConnector } from './connector/onTopOfConnector';
import { StopConnectorAtTheTop } from './connector/stopConnectorAtTheTop';

// This is the amount of space that should be above the stepper progress indicator to make the indicator in line with the text
const SPACING_ABOVE_PROGRESS_INDICATOR = 25;

const useStyles = buildUseStyles('PlanSummaryPhaseSteps', (theme) => ({
  root: {
    background: 'transparent !important', // Overwrite MUI's stepper background color - we want to let the bone background bleed through
    padding: '0 !important', // Overwrite MUI's stepper padding
    overflow: 'hidden', // Important to stop the connector from being visible
    gap: theme.spacing(3), // Spaces out the steps. Normally they're spaced out by the connectors, but the default connectors had to be removed to match the design

    // Per the design, the step icons need to be in the exact same vertical placement everywhere, including the summaries at the end.
    // In order to do that, we have to manually position them
    '& .MuiStepLabel-iconContainer': {
      position: 'relative', // The connectors use position:absolute to be able to go all the way up the list uninterrupted, so they need something to base themselves off of
      alignSelf: 'flex-start',
      marginTop: SPACING_ABOVE_PROGRESS_INDICATOR,
      marginRight: theme.spacing(2), // Adds horizontal spacing like in the design
    },
  },
}));

export function PlanSummaryPhaseSteps({ value }) {
  const classes = useStyles();
  const { onExpandedChange, isExpanded } = useExpandedSteps();
  const theme = useTheme();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  // These are things that need to be known for the special connectors that were built. If you ever reuse this stepper elsewhere, you may need to adjust them
  const connectorDetails = {
    backgroundColor: theme.palette.facet.lightGray,
    height: SPACING_ABOVE_PROGRESS_INDICATOR,
  };

  return (
    <Stepper
      orientation="vertical"
      className={classes.root}
      connector={null} // See the content class for more info on why the connector was removed
    >
      {value.map((step, i) => {
        const expanded = isExpanded(step.id());
        const progress = (
          <OnTopOfConnector>
            <SubtleProgressPie percentage={step.attr('completionPercentage')} />
          </OnTopOfConnector>
        );
        return (
          <Step key={step.id()}>
            <StepLabel
              StepIconComponent={() => {
                switch (i) {
                  // First will have a connector going above it from the last one, so it needs to stop the connector at the top
                  case 0:
                    return <><StopConnectorAtTheTop {...connectorDetails} />{progress}</>;
                  // Last one needs to start the connector
                  // DO NOT CHANGE THE ORDER OF THIS SWITCH/CASE! If there's only 1, and it's a BottomUpConnector, it will look bad
                  case value.length - 1:
                    return <><BottomUpConnector />{progress}</>;
                  default:
                    return progress;
                }
              }}
            >
              <StepPicker
                resource={step}
                expanded={expanded}
                onExpandedChange={(val) => {
                  logStandardizedEvent(categories.foundations, eventTypes.click, eventSubTypes.roadPlanItem, {
                    eventProperties: {
                      itemName: step.attr('title'),
                      isExpanded: !expanded,
                    },
                  });

                  onExpandedChange(step.id(), val);
                }}
              />
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
}

PlanSummaryPhaseSteps.defaultProps = {
};

PlanSummaryPhaseSteps.propTypes = {
  value: PropTypes.array.isRequired,
};
